import { useId, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { API_ERROR_CODES } from '@solar/contracts';

import { Alert } from '../../components/Alert';
import { useAuth } from '../../features/auth/auth-context';
import { ApiError } from '../../lib/api-error';
import { toDisplayMessage } from '../../lib/error-message';
import { paths } from '../../routes/paths';

import styles from './AuthForm.module.css';
import { EyeIcon, LockIcon, MailIcon } from './AuthIcons';
import { LoginLayout } from './LoginLayout';

interface LocationState {
  from?: string;
}

export function LoginPage(): React.JSX.Element {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setEmailNotVerified(false);
    setBusy(true);

    try {
      await signIn(email, password);

      const state = location.state as LocationState | null;
      void navigate(state?.from ?? paths.dashboard, { replace: true });
    } catch (caught: unknown) {
      setError(toDisplayMessage(caught, 'Unable to sign in. Please try again.'));
      setEmailNotVerified(
        caught instanceof ApiError && caught.code === API_ERROR_CODES.EMAIL_NOT_VERIFIED,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <LoginLayout
      title={
        <>
          Welcome back
          <span className="visually-hidden"> — Sign in</span>
        </>
      }
      subtitle="Sign in to your account to continue your solar journey."
    >
      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && <Alert tone="error">{error}</Alert>}
        {emailNotVerified && (
          <p className={styles.contextPrompt}>
            <Link to={paths.resendVerification}>Resend verification email</Link>
          </p>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor={emailId}>
            Email address
          </label>
          <div className={styles.inputShell}>
            <MailIcon />
            <input
              id={emailId}
              className={styles.input}
              type="email"
              name="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              autoComplete="email"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={passwordId}>
            Password
          </label>
          <div className={`${styles.inputShell} ${styles.passwordShell}`}>
            <LockIcon />
            <input
              id={passwordId}
              className={styles.input}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              autoComplete="current-password"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              className={styles.togglePassword}
              aria-pressed={showPassword}
              aria-label="Toggle visibility"
              title={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => {
                setShowPassword((visible) => !visible);
              }}
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
          <div className={styles.forgotRow}>
            <Link className={styles.forgotLink} to={paths.forgotPassword}>
              Forgot password?
            </Link>
          </div>
        </div>

        <button className={styles.submitButton} type="submit" disabled={busy} aria-busy={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
          {!busy && (
            <span className={styles.submitArrow} aria-hidden="true">
              →
            </span>
          )}
        </button>
      </form>

      <p className={styles.footerPrompt}>
        New to Seamontech? <Link to={paths.register}>Create a customer account</Link>
      </p>
    </LoginLayout>
  );
}
